import { useRef, useState, type KeyboardEvent } from 'react'
import type { Snippet, SnippetFormat } from '@/share/embedSnippet'
import { writeText } from './ShareSheet'

/**
 * Three snippets, one visible at a time, with a copy button under whichever is showing.
 *
 * Extracted from `EmbedBadge` when F33 put the same three snippets at the foot of every status page
 * (`ui/status/ReadmeBlock.tsx`). Two copies of a roving-tabindex implementation is two chances to
 * get the keyboard contract half right, and the half that goes missing is always the one nobody
 * tests by hand.
 *
 * A real tab pattern rather than three buttons with `aria-pressed`: the code block below is a panel
 * whose content the tabs *replace*, which is exactly what `role="tab"` means to a screen reader, and
 * the arrow-key contract that comes with it is the reason the roving `tabIndex` below is not
 * decoration.
 *
 * `idPrefix` exists because both surfaces can be on the screen at once — the share sheet opens over
 * a status page — and two elements with `id="mha-embed-panel"` would make `aria-controls` point at
 * whichever the browser found first.
 */
export function SnippetTabs({
  idPrefix,
  snippets,
  initial,
  onCopied,
}: {
  idPrefix: string
  snippets: readonly Snippet[]
  initial?: SnippetFormat
  /** Called after every copy attempt, so each surface can announce it in its own words. */
  onCopied: (snippet: Snippet, copied: boolean) => void
}) {
  const [active, setActive] = useState<SnippetFormat>(initial ?? snippets[0]!.format)
  const tabs = useRef<HTMLDivElement>(null)
  const current = snippets.find((snippet) => snippet.format === active) ?? snippets[0]!
  const panelId = `${idPrefix}-panel`

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(event.key)) return
    event.preventDefault()
    const index = snippets.findIndex((snippet) => snippet.format === active)
    const last = snippets.length - 1
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowLeft'
            ? (index + last) % snippets.length
            : (index + 1) % snippets.length
    const chosen = snippets[next]!
    setActive(chosen.format)
    // Focus follows selection, which is the automatic-activation half of the tabs pattern and
    // what a keyboard user expects when the panel changes on arrow alone.
    tabs.current?.querySelector<HTMLElement>(`[data-format="${chosen.format}"]`)?.focus()
  }

  return (
    <>
      <div className="mha-embed__tabs" role="tablist" ref={tabs} onKeyDown={onKeyDown}>
        {snippets.map((snippet) => (
          <button
            key={snippet.format}
            type="button"
            role="tab"
            data-format={snippet.format}
            id={`${idPrefix}-tab-${snippet.format}`}
            aria-selected={snippet.format === active}
            aria-controls={panelId}
            tabIndex={snippet.format === active ? 0 : -1}
            className="mha-embed__tab"
            onClick={() => setActive(snippet.format)}
          >
            {snippet.label}
          </button>
        ))}
      </div>

      <div
        className="mha-embed__panel"
        role="tabpanel"
        id={panelId}
        aria-labelledby={`${idPrefix}-tab-${current.format}`}
        tabIndex={0}
      >
        <p className="mha-embed__hint">{current.hint}</p>
        {/* `overflow-x: auto` on the block, never on the page: a 200-character snippet must not
            make the whole dialog scroll sideways on a phone.
            `tabIndex` because of that overflow: at 360 px the snippet is wider than its box, and a
            scrollable region a keyboard cannot reach is content a keyboard cannot read (axe's
            `scrollable-region-focusable`, which this failed at 360 px before it was added). */}
        <pre className="mha-embed__code" tabIndex={0}>
          <code>{current.code}</code>
        </pre>
        <button
          type="button"
          className="mha-share__action"
          onClick={() => {
            void writeText(current.code).then((copied) => onCopied(current, copied))
          }}
        >
          Copy {current.label}
        </button>
      </div>
    </>
  )
}
